import React from 'react';
import styles from 'v2/common/DataVendorModals/modal.module.scss';
import { FormControl } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { getRootDomain } from 'v2/utils/formValidator';
import restFactory from 'v2/utils/restFactory';
import { DataVendor } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import classnames from 'classnames';
import { PostProcessKeys, TranslationKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  show: boolean;
  dataVendor: DataVendor;
  onClose: (dataVendor?: DataVendor) => void;
}

const Modal = ({ show, dataVendor, onClose }: ModalProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { isValid, errors },
    handleSubmit,
    register,
    setError,
  } = useForm({
    mode: 'onChange',
    defaultValues: { domain: dataVendor.domain },
  });

  const { isLoading: isMutationLoading, mutate: domainMutate } = useMutation(
    async (domain: string) => {
      return await restFactory.patch<DataVendor>(
        `/dj/api/v1/data_vendor/${dataVendor.id}/`,
        { domain }
      );
    },
    {
      onSuccess: (data: DataVendor) => {
        toast.success(
          `${t(TranslationKeys.dataProvider_one, {
            postProcess: PostProcessKeys.Capitalize,
          })} domain saved.`
        );
        onClose(data);
      },
      onError: () => {
        setError('domain', {
          type: 'manual',
          message: `This website already belongs to a ${t(
            TranslationKeys.company_one
          )} in your database.`,
        });
      },
    }
  );

  const onSubmit = async ({ domain }: { domain: string }) => {
    if (isValid) {
      domainMutate(domain);
    }
  };

  return (
    <ConfirmationModal
      className={styles.viewModals}
      centered={true}
      animation={true}
      show={show}
      onCancel={() => onClose()}
      okBtnText="Save"
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      isLoading={isMutationLoading}
      title="Edit Website"
      onEntered={() => {
        control._fields.domain?._f.ref.focus?.();
      }}
    >
      <div>
        <div className={classnames(styles.dataVendorForm)}>
          <FormLabel label={`Website`} required={false} />
          <div>
            <FormControl
              id="domain"
              className={styles.inputText}
              type="text"
              autoComplete="off"
              isInvalid={!!errors.domain}
              placeholder="Domain Name"
              aria-label="domain_input"
              {...register('domain', {
                required: true,
                validate: (value) =>
                  dataVendor.domain === value ||
                  getRootDomain(value) !== '' ||
                  'This is not a valid website address',
              })}
            />
            <FormControl.Feedback type="invalid">
              {errors.domain?.message}
            </FormControl.Feedback>
          </div>
        </div>
      </div>
    </ConfirmationModal>
  );
};

export default Modal;
